import { useTranslation } from "react-i18next";
import { ChevronRight, Columns2 } from "lucide-react";
import { useSelector } from "react-redux";
import { Popover, PopoverButton, PopoverPanel, Switch } from "@headlessui/react";
import { fromJS } from "immutable";
import { resolveBucketColor } from "../../../board/components/Lane/LaneHeader";

interface VisibleColumnProps {
  contentType: string;
  onChange: (columnIds: string[]) => void;
}

const VisibleColumn = ({ contentType, onChange }: VisibleColumnProps) => {
  const { t } = useTranslation();

  const buckets = useSelector((state: any) => state.bucketReducer.get(contentType));
  const tasks = useSelector((state: any) => state.taskReducer.getIn(["BOARD", "children"]));
  const users = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));
  const groupBy = useSelector((state: any) => state.taskReducer.get("groupBy"));
  const groupByVisibleFieldIds =
    useSelector((state: any) => state.taskReducer.get("groupByVisibleFieldIds")) || fromJS([]);
  const groups = useSelector((state: any) => state.taskReducer.get(groupBy));

  const laneItems = () => {
    let groups;
    switch (groupBy) {
      case "assignees":
        const userGroups = users.map((assignee: any) => ({
          id: assignee.get("accountId"),
          name: assignee.get("displayName"),
          imageUrl: assignee.get("avatarUrl"),
        }));
        groups = [{ id: "", name: "Unassigned" }, ...userGroups];
        break;

      case "bucketId":
        groups =
          buckets.size > 0
            ? [{ id: "", name: t("No stage"), defaultColor: "#9ca3af" }, ...buckets.toJS()]
            : [{ id: "", name: t("No stage"), defaultColor: "#9ca3af" }];
        break;
      default:
        groups = [];
    }

    return groups;
  };

  const groupTasks = (groupItemId: string) => {
    if (groupBy === "assignees") {
      return tasks.filter((task: any) => {
        const taskAssignees = task.getIn(["data", "assignees", "value"]) || fromJS([]);
        if (taskAssignees.size > 0) {
          return taskAssignees.toJS().includes(groupItemId);
        } else if (groupItemId == "") {
          return true;
        }
      });
    }
    if (groupBy === "bucketId") {
      return tasks.filter((task: any) => {
        const bucketValue = task.getIn(["data", "bucketId", "value"]) || "";
        return bucketValue == groupItemId;
      });
    }
    return groups.getIn([groupItemId]);
  };

  const laneWithData = laneItems().map((item: any) => {
    const { id, name, imageUrl, color, defaultColor, type = contentType } = item;
    const cards: any = [];
    if (groups) {
      const groupByItems = groupTasks(id);
      if (groupByItems) {
        groupByItems.map((task: any) => {
          cards.push({
            id: task.get("id"),
            title: task.getIn(["data", "name", "value"]),
            description: task.getIn(["data", "notes", "value"]),
            draggable: true,
            finishDate: task.getIn(["data", "finish", "value"]),
            task,
            users,
          });
        });
      }
    }
    return { id, title: name, type, imageUrl, color, defaultColor, cards };
  });

  const renderVisibleColumn = () => {
    const hideColumns = groupByVisibleFieldIds.toJS();
    return laneWithData.map((item, index) => {
      return (
        <div key={`${item.id}`}>
          <div className="w-full flex items-center justify-between space-x-2">
            <div className="w-full flex items-center gap-2 text-sm font-medium px-3 py-2">
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{
                  backgroundColor: item.defaultColor || resolveBucketColor(item.color, index),
                }}
              />
              {item.title}
            </div>
            <Switch
              checked={!hideColumns.includes(item.id)}
              onChange={(checked) => {
                if (checked) {
                  onChange(hideColumns.filter((column) => column !== item.id));
                } else {
                  onChange([...hideColumns, item.id]);
                }
              }}
              className={`${
                !hideColumns.includes(item.id) ? "bg-blue-600" : "bg-gray-200"
              } relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2`}
            >
              <span
                aria-hidden="true"
                className={`${
                  !hideColumns.includes(item.id) ? "translate-x-4" : "translate-x-0"
                } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </Switch>
          </div>
          {item.defaultColor && <hr className="border-gray-200 my-1" />}
        </div>
      );
    });
  };

  return (
    <div>
      <Popover>
        <PopoverButton className="w-full flex items-center justify-between space-x-2 p-2 cursor-default rounded-md hover:bg-gray-100 outline-none">
          <div className="flex items-center space-x-2">
            <div>
              <Columns2 className="w-5 h-5" />
            </div>
            <div className="text-nowrap text-sm font-medium">{t("Visible columns")}</div>
          </div>
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-2">
              <div className="w-max px-1 rounded-full text-center text-xs text-white bg-gray-600">
                {laneWithData.length - groupByVisibleFieldIds.size}
              </div>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </PopoverButton>
        <PopoverPanel anchor="right start" className="[--anchor-gap:12px] z-20">
          <div className="w-max max-h-120 overflow-y-auto p-2 rounded-md bg-white border border-gray-300">
            {renderVisibleColumn()}
          </div>
        </PopoverPanel>
      </Popover>
    </div>
  );
};

export default VisibleColumn;
